"use client";

import { hoverPreviewHandlers } from "@saas/shared/assets/hover-preview";
import {
	getVideoRowEtaLabel,
	getVideoRowModelLabel,
} from "@saas/shared/history/video-model-label";
import { Button } from "@ui/components/button";
import { cn } from "@ui/lib";
import {
	EditIcon,
	MusicIcon,
	PlayIcon,
	RefreshCwIcon,
	StarIcon,
	VideoIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";
import type { ImageActionLabels } from "./HistoryImageActions";
import { HistoryImageActions } from "./HistoryImageActions";
import type { HistoryStatusLabels, VideoHistoryItem } from "./types";
import { DEFAULT_HISTORY_STATUS_LABELS, formatHistoryDate } from "./types";
import {
	getVideoDisplayPrompt,
	getVideoEffect,
	useVideoHomeRouter,
} from "./video-home";

interface VideoHistoryCardProps {
	/** One submission. More than one clip when the user asked for several
	 *  outputs — they share a prompt, settings and timestamp, so they get one
	 *  card with one metadata line. */
	items: VideoHistoryItem[];
	/** Actions are only rendered on the video generator page, where the form
	 *  they load the generation back into actually exists. */
	onReprompt?: (item: VideoHistoryItem) => void;
	onRegenerate?: (item: VideoHistoryItem) => void;
	isRegenerateDisabled?: boolean;
	statusLabels?: HistoryStatusLabels;
	/** 点成片打开详情弹窗 */
	onOpenDetail?: (item: VideoHistoryItem) => void;
	/** hover 工具条的文案，跟图片卡共用一套 */
	actionLabels: ImageActionLabels;
	/** 省略就只留下载。删除跟图片一样收在"…"菜单里 */
	onToggleFavorite?: (item: VideoHistoryItem) => void;
	isFavorite?: (item: VideoHistoryItem) => boolean;
	onDelete?: (item: VideoHistoryItem) => void;
}

/** Worst-case wins: the batch is only "succeeded" once every clip is in. */
function batchStatus(items: VideoHistoryItem[]) {
	if (
		items.some(
			(item) => item.status !== "SUCCESS" && item.status !== "FAILED",
		)
	) {
		return "GENERATING";
	}
	if (items.every((item) => item.status === "FAILED")) {
		return "FAILED";
	}
	return "SUCCESS";
}

/** 特效页固定挂的参考片 / 原声都在 R2 的 presets/ 下，是内部素材，不给用户看（2026-10-28 老公定） */
const isPresetMedia = (url: string) => url.includes("/presets/");

/** 成片卡的框（VideoClip 的 max-w / max-h），占位按同一个框缩，成片到了尺寸不跳 */
const CLIP_BOX = { width: 378, height: 260 };

function ClipSkeleton({ item }: { item: VideoHistoryItem }) {
	// 首帧 / 首尾帧：视频就从第 1 张图开始，糊一层首帧有意义。多图参考、文生的第 1 张图不是首帧，
	// 糊上去像在重做那张图（10/23 老公定）。没记 mode 的老行都是单图
	const mode = item.metadata?.mode;
	const frameUrl =
		!mode || mode === "start" || mode === "between"
			? item.inputImageUrl
			: "";
	const [ratioWidth, ratioHeight] = (item.metadata?.aspectRatio ?? "")
		.split(":")
		.map(Number);
	// 比例是 auto（成片跟着输入图走）时只能按输入图占位
	const hasRatio = ratioWidth > 0 && ratioHeight > 0;
	const scale = hasRatio
		? Math.min(CLIP_BOX.width / ratioWidth, CLIP_BOX.height / ratioHeight)
		: 0;
	return (
		<div className="relative inline-block max-w-full overflow-hidden rounded-xl bg-zinc-900/5 dark:bg-black/30">
			{hasRatio ? (
				<div
					style={{
						width: ratioWidth * scale,
						aspectRatio: `${ratioWidth} / ${ratioHeight}`,
					}}
					className="max-w-full"
				>
					{frameUrl ? (
						<img
							src={frameUrl}
							alt=""
							aria-hidden="true"
							className="h-full w-full object-cover opacity-25 blur-[2px]"
						/>
					) : null}
				</div>
			) : frameUrl ? (
				<img
					src={frameUrl}
					alt=""
					aria-hidden="true"
					className="block h-auto w-auto max-h-[260px] max-w-[378px] object-contain opacity-25 blur-[2px]"
				/>
			) : (
				<div className="aspect-video w-[min(378px,70vw)]" />
			)}
			<div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
				<span className="loading loading-spinner loading-md text-primary" />
				<span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">
					Generating…
				</span>
			</div>
		</div>
	);
}

const formatClipDuration = (seconds: number) =>
	`${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, "0")}`;

/** 卡片里的成片：不挂原生 controls —— 悬停自动播放、移出复位，点击留给详情弹窗。
 *  移动端没有 hover，就停在首帧，点一下进详情看完整播放器。 */
function VideoClip({
	item,
	onOpenDetail,
	actionLabels,
	onToggleFavorite,
	isFavorite,
	onDelete,
}: {
	item: VideoHistoryItem;
	onOpenDetail?: (item: VideoHistoryItem) => void;
	actionLabels: ImageActionLabels;
	onToggleFavorite?: (item: VideoHistoryItem) => void;
	isFavorite?: boolean;
	onDelete?: (item: VideoHistoryItem) => void;
}) {
	return (
		<div
			className="relative group inline-block rounded-xl overflow-hidden bg-black/5 dark:bg-black/20 max-w-full"
			{...hoverPreviewHandlers}
		>
			{/* 不设 poster：输入图的比例未必等于成片比例（单图输入时上游按 auto
			    自己定），拿它当封面会先撑成竖的、播放时再跳。让 video 按 metadata
			    的真实宽高自适应。#t=0.001 让 Safari 也渲染首帧而不是黑屏 */}
			{/* biome-ignore lint/a11y/useMediaCaption: AI-generated clips have no caption track */}
			<video
				src={`${item.videoUrl}#t=0.001`}
				muted
				loop
				playsInline
				preload="metadata"
				className="block w-auto h-auto max-w-[378px] max-h-[260px]"
			/>

			{onOpenDetail && (
				<button
					type="button"
					aria-label="Open details"
					className="absolute inset-0 cursor-pointer"
					onClick={() => onOpenDetail(item)}
				/>
			)}

			{/* 时长常驻左下角，替代被去掉的进度条 */}
			<span className="pointer-events-none absolute bottom-2 left-2 rounded bg-black/60 px-1.5 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm">
				{formatClipDuration(item.duration)}
			</span>

			{/* 工具条压在点击层之上，点它们不该开详情。跟图片卡共用同一个组件，
			    删除同样收在"…"里——它跟下载/收藏不是一个量级的操作 */}
			<HistoryImageActions
				labels={actionLabels}
				onDownload={() => {
					window.location.href = `/api/proxy-download?url=${encodeURIComponent(item.videoUrl ?? "")}`;
				}}
				onDelete={onDelete ? () => onDelete(item) : undefined}
				onFavorite={
					onToggleFavorite ? () => onToggleFavorite(item) : undefined
				}
				isFavorite={isFavorite}
			/>
		</div>
	);
}

export function VideoHistoryCard({
	items,
	onReprompt,
	onRegenerate,
	isRegenerateDisabled,
	statusLabels = DEFAULT_HISTORY_STATUS_LABELS,
	onOpenDetail,
	actionLabels,
	onToggleFavorite,
	isFavorite,
	onDelete,
}: VideoHistoryCardProps) {
	const first = items[0];
	const status = batchStatus(items);
	const finished = items.filter(
		(item) => item.status === "SUCCESS" && item.videoUrl,
	);
	// 支持多图之前的记录只有 inputImageUrl，用它兜底；文生视频的行是空串，滤掉
	const inputImages = (
		first.inputImageUrls?.length
			? first.inputImageUrls
			: [first.inputImageUrl]
	).filter(Boolean);
	const referenceVideos = (first.metadata?.referenceVideoUrls ?? []).filter(
		(url) => !isPresetMedia(url),
	);
	const referenceAudios = (first.metadata?.referenceAudioUrls ?? []).filter(
		(url) => !isPresetMedia(url),
	);
	const t = useTranslations();
	const effect = getVideoEffect(first);
	const prompt = getVideoDisplayPrompt(first);
	// 不是本页出的片，Reprompt / Regenerate 回它出生的页面去做，见 video-home.ts
	const routeHome = useVideoHomeRouter();

	return (
		<div className="transition-all bg-white dark:bg-card border border-zinc-200/20 dark:border-border/10 rounded-xl p-3 sm:p-6 hover:shadow-sm">
			{/* Metadata Line */}
			<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3 sm:mb-4">
				<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] sm:text-xs text-zinc-500">
					<div className="flex items-center space-x-1.5 font-bold text-zinc-700 dark:text-zinc-300">
						{effect ? (
							<StarIcon className="h-3.5 w-3.5" />
						) : (
							<VideoIcon className="h-3.5 w-3.5" />
						)}
						<span>
							{effect
								? `EFFECT · ${t(effect.titleKey)}`
								: "AI VIDEO"}
						</span>
					</div>
					<span className="hidden sm:inline text-zinc-300">|</span>
					<span>{formatHistoryDate(first.createdAt)}</span>
					<span className="hidden sm:inline text-zinc-300">|</span>
					<span className="font-medium">
						{getVideoRowModelLabel(first)}
					</span>
					<span className="hidden sm:inline text-zinc-300">|</span>
					<span>
						{first.resolution} · {first.duration}s
					</span>
					{items.length > 1 && (
						<>
							<span className="hidden sm:inline text-zinc-300">
								|
							</span>
							<span>{items.length}x</span>
						</>
					)}
				</div>
				{/* 成功不出徽章——视频就在下面摆着，再标一句"成功"是废话 */}
				{status !== "SUCCESS" && (
					<div
						className={cn(
							"self-start sm:self-auto text-xs sm:text-[11px] font-semibold px-2.5 py-0.5 rounded-md",
							status === "FAILED"
								? "bg-red-50 text-red-600"
								: "bg-blue-50 text-blue-600",
						)}
					>
						{status === "FAILED"
							? statusLabels.failed
							: statusLabels.generating}
					</div>
				)}
			</div>

			{/* Prompt with the input image */}
			<div className="mb-4 sm:mb-5 flex flex-col sm:flex-row gap-3 sm:gap-4 items-start">
				<div className="flex flex-shrink-0 flex-wrap gap-1.5">
					{inputImages.map((url, index) => (
						<div
							key={url}
							className="relative rounded-lg overflow-hidden bg-zinc-50 w-14 h-14 sm:w-[72px] sm:h-[72px]"
						>
							<img
								src={url}
								alt={`Input ${index + 1}`}
								className="w-full h-full object-cover"
							/>
						</div>
					))}
					{/* 参考视频和输入图排在一起，角上一个播放标区分 */}
					{referenceVideos.map((url, index) => (
						<div
							key={url}
							className="relative rounded-lg overflow-hidden bg-zinc-50 w-14 h-14 sm:w-[72px] sm:h-[72px]"
						>
							<video
								src={`${url}#t=0.1`}
								muted
								playsInline
								preload="metadata"
								aria-label={`Reference video ${index + 1}`}
								className="w-full h-full object-cover"
							/>
							<span className="absolute bottom-1 left-1 flex size-4 items-center justify-center rounded-full bg-black/60 text-white">
								<PlayIcon className="size-2.5 fill-current" />
							</span>
						</div>
					))}
					{referenceAudios.map((url, index) => (
						<div
							key={url}
							role="img"
							aria-label={`Reference audio ${index + 1}`}
							className="flex items-center justify-center rounded-lg bg-zinc-100 text-zinc-500 w-14 h-14 sm:w-[72px] sm:h-[72px]"
						>
							<MusicIcon className="h-5 w-5" />
						</div>
					))}
				</div>
				{prompt && (
					<div className="flex-1 min-w-0">
						<p className="text-[15px] text-zinc-800 dark:text-zinc-200 leading-relaxed line-clamp-3">
							{prompt}
						</p>
					</div>
				)}
			</div>

			{/* One tile per clip — sized by the clip's own aspect ratio */}
			<div className="flex flex-wrap gap-3">
				{items.map((item) =>
					item.status === "SUCCESS" && item.videoUrl ? (
						<VideoClip
							key={item.id}
							item={item}
							onOpenDetail={onOpenDetail}
							actionLabels={actionLabels}
							onToggleFavorite={onToggleFavorite}
							isFavorite={isFavorite?.(item)}
							onDelete={onDelete}
						/>
					) : item.status === "FAILED" ? (
						<div
							key={item.id}
							className="p-4 bg-red-50/50 border border-red-100 rounded-lg text-sm text-red-600"
						>
							{item.errorMessage || "Generation failed"}
						</div>
					) : (
						<ClipSkeleton key={item.id} item={item} />
					),
				)}
			</div>

			{status === "GENERATING" && (
				<p className="mt-2 text-xs text-muted-foreground">
					{getVideoRowEtaLabel(first)}
				</p>
			)}

			{/* Actions — need a finished clip to load back into the form */}
			{finished.length > 0 && (onReprompt || onRegenerate) && (
				<div className="mt-4 flex flex-wrap gap-3">
					{onReprompt && (
						<Button
							variant="outline"
							onClick={() =>
								routeHome(first, "reprompt") ||
								onReprompt(first)
							}
							className="h-9 px-4 text-xs font-medium border-zinc-200 text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900 rounded-lg gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							<EditIcon className="h-3.5 w-3.5" />
							Reprompt
						</Button>
					)}
					{onRegenerate && (
						<Button
							variant="outline"
							disabled={isRegenerateDisabled}
							onClick={() =>
								routeHome(first, "regenerate") ||
								onRegenerate(first)
							}
							className="h-9 px-4 text-xs font-medium border-zinc-200 text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900 rounded-lg gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							<RefreshCwIcon className="h-3.5 w-3.5" />
							Regenerate
						</Button>
					)}
				</div>
			)}
		</div>
	);
}
